import { useRef, useState } from "react";
import { FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadFile, errMsg } from "./kpiFormat";

const ENTITIES = {
  ad_spend: "Weekly ad spend",
  leads: "Leads / enquiries",
  transactions: "Sales & payments",
  entitlements: "Memberships & packs",
  attendance: "Attendance",
};

export const CsvImportPanel = ({ columns, onImported }) => {
  const [entity, setEntity] = useState("ad_spend");
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("reason", reason);
      const r = await api.post(`/kpi/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(r.data);
      toast.success(`${r.data.imported} row(s) imported`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onImported?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-6 space-y-4" data-testid="kpi-csv-import-panel">
      <div>
        <h3 className="font-display text-lg font-semibold">CSV import</h3>
        <p className="text-sm text-zinc-500">Fallback for anything the integrations can't read yet. Rows are matched on their id
          column, so re-importing a corrected file updates rather than duplicates. Every import needs a reason and is audited.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        <div className="space-y-1.5">
          <Label>What are you importing?</Label>
          <Select value={entity} onValueChange={(v) => { setEntity(v); setResult(null); }}>
            <SelectTrigger data-testid="kpi-import-entity"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ENTITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <button type="button" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
            onClick={() => downloadFile(`/kpi/import/${entity}/template`, {}, `kpi_${entity}_template.csv`).catch((e) => toast.error(errMsg(e)))}
            data-testid="kpi-import-template-btn"><FileDown className="h-3.5 w-3.5" />Download template</button>
        </div>
        <div className="space-y-3">
          <div className="text-xs text-zinc-500 font-mono-num break-all" data-testid="kpi-import-columns">{(columns?.[entity] || []).join(", ")}</div>
          <Input ref={inputRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="kpi-import-file" />
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. Ads Manager export for October)"
            data-testid="kpi-import-reason" />
          <Button size="sm" disabled={busy || !file || reason.trim().length < 3} onClick={run} data-testid="kpi-import-submit-btn">
            <Upload className="h-4 w-4 mr-1.5" />{busy ? "Importing…" : "Import"}
          </Button>
        </div>
      </div>
      {result && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm" data-testid="kpi-import-result">
          <b>{result.imported}</b> row(s) imported{result.errors?.length ? `, ${result.errors.length} skipped:` : "."}
          {result.errors?.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-red-700">{result.errors.slice(0, 10).map((e) => <li key={e.row}>Row {e.row}: {e.error}</li>)}</ul>
          )}
        </div>
      )}
    </section>
  );
};
